import { Card, Theme, Typography, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { ReactElement, useState, useEffect } from "react";
import Translator from "../services/Translator";
import { Equalizer as ConfidenceIcon, AspectRatio as SizeIcon } from "@material-ui/icons";
import { getFilterProperties, isMatchingDamage } from "../services/Utility";
import { Filter, ViImagePartDamage, ViSide, ViDamageType } from "../models/";

const useStyles = makeStyles((theme: Theme) => ({
  card: ({
    focus,
    highlighted = false,
    isFilterSet = false,
  }: {
    focus?: boolean;
    highlighted?: boolean;
    isFilterSet?: boolean;
  }) => ({
    cursor: "pointer",
    padding: "0.382rem",
    margin: "0.382rem",
    ...(focus === false && {
      position: "absolute",
      display: "none",
      opacity: 0,
      top: "1rem",
      left: "1rem",
    }),
    transition: "all 0.15s ease-out",
    transform: highlighted ? "scale(1.05)" : "scale(1)",
    opacity: isFilterSet && highlighted === false ? 0.618 : 1,
  }),
  damageType: {
    fontWeight: "bold",
    color: <T extends keyof typeof theme.damageTypes>({
      damageType,
    }: {
      damageType?: T;
    }) => theme.damageTypes[damageType || "default"],
  },
  damageMeta: {
    fontSize: "0.8rem",
    lineHeight: "24px",
    "& svg": {
      marginRight: "0.382rem",
      verticalAlign: "bottom",
    },
    "& + & svg": {
      marginLeft: "1rem",
    },
  },
  part: {
    fontWeight: "bold",
  },
}));

function DamageDetailCard({
  imgPartDamage,
  focus,
  hoverFilter,
  setHoverFilter,
}: {
  imgPartDamage: ViImagePartDamage;
  focus?: boolean;
  hoverFilter: Filter[];
  setHoverFilter?: (filters: Filter[]) => void;
}): ReactElement {
  const [highlighted, setHighlighted] = useState<boolean | undefined>(undefined);
  const classes = useStyles({
    focus,
    damageType: imgPartDamage.aggregatedPartDamage.damageType,
    highlighted,
    isFilterSet: Boolean(hoverFilter.length),
  });

  function handleHover(hovering: boolean) {
    if (setHoverFilter)
      hovering
        ? setHoverFilter(getFilterProperties([imgPartDamage]))
        : setHoverFilter([]);
  }

  useEffect(() => {
    setHighlighted(
      (hoverFilter.length &&
        hoverFilter.some((filter) => isMatchingDamage(filter, imgPartDamage))) ||
        false
    );
  }, [hoverFilter, imgPartDamage, focus]);

  return (
    // <Flipped flipId={`damageCard-${imgPartDamage.damageId}`} stagger="damageCards">
    <Card
      className={classes.card}
      onMouseEnter={() => {
        handleHover(true);
      }}
      onMouseLeave={() => {
        handleHover(false);
      }}
    >
      <Typography variant="body2">
        <span className={classes.damageType}>
          {Translator.translate(
            `damagetypes.${imgPartDamage.imageDamage.type.toLowerCase()}`
          )}
        </span>
        &nbsp;
        {imgPartDamage.imageDamage.type.toUpperCase() === ViDamageType.MISSING ? (
          ""
        ) : (
          <span>on</span>
        )}
        <span className={classes.part}>
          {!imgPartDamage.aggregatedPartDamage.partIdentifiers.side ||
          imgPartDamage.aggregatedPartDamage.partIdentifiers.side !== ViSide.NONE
            ? Translator.translate(
                `sides.${imgPartDamage.aggregatedPartDamage.partIdentifiers.side.toLowerCase()}`
              )
            : ""}{" "}
          {Translator.translate(
            `parts.${imgPartDamage.aggregatedPartDamage.partIdentifiers.genericPartName.toLowerCase()}`
          )}
        </span>
      </Typography>
      <div>
        <Tooltip
          title="Relative Size"
          enterDelay={618}
          leaveDelay={100}
          placement="right"
          arrow
        >
          <Typography component="span" className={classes.damageMeta}>
            <SizeIcon />
            {`${Math.max(1, Math.round(imgPartDamage.relativeSize * 100))}%`}
          </Typography>
        </Tooltip>
        <Tooltip
          title="Confidence"
          enterDelay={618}
          leaveDelay={100}
          placement="right"
          arrow
        >
          <Typography component="span" className={classes.damageMeta}>
            <ConfidenceIcon />
            {`${Math.max(1, Math.round(imgPartDamage.aggregatedPartDamage.p * 100))}%`}
          </Typography>
        </Tooltip>
      </div>
    </Card>
    // </Flipped>
  );
}

export default React.memo(DamageDetailCard, (prev, next) => !(prev.focus || next.focus));
